import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import NeedsAttention from '../components/NeedsAttention';
import DayBriefCard from '../components/DayBriefCard';
import GrowthCard from '../components/GrowthCard';
import PlanBuyCard from '../components/PlanBuyCard';
import MoneyToday from '../components/MoneyToday';
import WinterSummary from '../components/WinterSummary';
import {
  ChevronRight, DollarSign, Zap, TrendingUp,
  ChevronDown, Wrench, Users, BarChart2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { businessToday, fmtWindow } from '../utils/time';

function fmtMins(mins: number) {
  if (!mins) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function fmtMoney(val: number | null | undefined) {
  if (val == null) return '—';
  return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function roleLabel(role: string | null) {
  if (!role) return 'Member';
  const map: Record<string, string> = {
    leader: 'Leader', crew_leader: 'Leader', project_manager: 'PM',
    laborer: 'Laborer', skilled_laborer: 'Skilled', crew_member: 'Member',
  };
  return map[role] || role;
}

const statusColors: Record<string, string> = {
  scheduled:   'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  complete:    'bg-green-100 text-green-800',
  skipped:     'bg-gray-100 text-gray-500',
};

function todayISO() { return businessToday(); }

function effColor(pct: number | null) {
  if (pct == null) return 'text-gray-500';
  if (pct >= 100) return 'text-green-700';
  if (pct >= 85)  return 'text-emerald-600';
  if (pct >= 70)  return 'text-amber-600';
  return 'text-red-600';
}

export default function Dashboard() {
  const today = todayISO();
  const [effDateMode, setEffDateMode] = useState<'lifetime' | 'date'>('lifetime');
  const [effDate, setEffDate]         = useState(today);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [combosOpen, setCombosOpen]   = useState(false);
  const qcDash = useQueryClient();

  const { data: jobsData, isLoading } = useQuery(['jobs', 'today'], () =>
    api.get('/jobs', { params: { date: today } }).then(r => r.data.jobs as any[])
  );

  const { data: routesData } = useQuery(['routes', 'today'], () =>
    api.get('/routes', { params: { date: today } }).then(r => r.data.routes as any[])
  );

  const { data: statsData } = useQuery('dashboard-stats', () =>
    api.get('/dashboard/stats').then(r => r.data)
  );

  // Crew efficiency — lifetime by default, date-scoped when user switches
  const effQueryKey = effDateMode === 'lifetime'
    ? ['crew-efficiency', 'lifetime']
    : ['crew-efficiency', effDate];
  const { data: effData, isLoading: effLoading } = useQuery(
    effQueryKey,
    () => effDateMode === 'lifetime'
      ? api.get('/dashboard/crew-efficiency', { params: { all_time: true } }).then(r => r.data)
      : api.get('/dashboard/crew-efficiency', { params: { date: effDate } }).then(r => r.data)
  );

  const { data: comboData } = useQuery('crew-combos', () =>
    api.get('/dashboard/crew-combinations').then(r => r.data)
  );

  const { data: equipData } = useQuery('equipment-reports-open', () =>
    api.get('/equipment-reports', { params: { status: 'open' } }).then(r => r.data.reports as any[])
  );

  const resolveEquip = useMutation(async (reportId: number) => {
    await api.patch(`/equipment-reports/${reportId}/resolve`, {});
  }, {
    onSuccess: () => { qcDash.invalidateQueries('equipment-reports-open'); toast.success('Marked resolved'); },
    onError:   () => { toast.error('Failed to resolve'); },
  });

  const openReports: any[]   = equipData   || [];
  const jobs:        any[]   = jobsData    || [];
  const routes:      any[]   = routesData  || [];
  const effEmployees: any[]  = effData?.employees || [];
  const overallEff           = effData?.overallEfficiency as number | null | undefined;
  const combos:      any[]   = comboData?.combos || [];
  const stats                = statsData;

  // Group today's jobs by route (skipped excluded)
  const routeById: Record<number, any> = Object.fromEntries(routes.map(r => [r.id, r]));
  const jobsByRoute: Record<string, any[]> = {};
  const routeOrder: string[] = [];
  for (const job of jobs) {
    if (job.status === 'skipped') continue;
    const key = job.route_id ? String(job.route_id) : 'none';
    if (!jobsByRoute[key]) { jobsByRoute[key] = []; routeOrder.push(key); }
    jobsByRoute[key].push(job);
  }
  function isExpanded(key: string) { return expandedGroups[key] !== false; }
  function toggleGroup(key: string) { setExpandedGroups(prev => ({ ...prev, [key]: !isExpanded(key) })); }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-brand-700">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-gray-900 leading-none mt-1">
            {(() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; })()}
          </h1>
        </div>
      </div>

      {/* What is on today, and anything that has to be packed for it. */}
      <DayBriefCard />

      {/* How much money do I have — this morning (owner, Sep 28 2026). */}
      <MoneyToday />

      {/* ── Top 4 Revenue/Profit/Efficiency boxes ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* This week, Monday through today (owner, Sep 19 2026: "instead of this year rev I would rather this week").
            The year's total still rides underneath in small type. */}
        <RevenueCard
          label="This Week"
          primary={stats?.week_revenue}
          secondary={stats?.week_profit}
          secondaryLabel="profit"
          labor={stats?.week_labor}
          sub={stats ? `${stats.week_completed ?? 0} jobs${stats.week_from ? ` since ${stats.week_from}` : ''}${stats.week_revenue_mowing ? ` · ${fmtMoney(stats.week_revenue_mowing)} mowing` : ''} · year ${fmtMoney(stats.ytd_revenue || 0)}` : undefined}
          color="green"
        />
        {/* Monthly */}
        <RevenueCard
          label="This Month"
          primary={stats?.month_revenue}
          secondary={stats?.month_profit}
          secondaryLabel="profit"
          labor={stats?.month_labor}
          sub={stats ? `${stats.month_completed} done · ${fmtMoney(stats.month_projected)} projected` : undefined}
          color="emerald"
        />
        {/* Daily */}
        <RevenueCard
          label="Today"
          primary={stats?.today_revenue}
          secondary={stats?.today_profit}
          secondaryLabel="profit"
          labor={stats?.today_labor}
          sub={stats ? `${stats.today_completed}/${stats.today_jobs} done` : undefined}
          color="violet"
        />
        {/* Efficiency summary */}
        <div className="card p-4 sm:p-5">
          <div className="flex items-start justify-between">
            <div className="text-xs font-black uppercase tracking-wide text-gray-600">Efficiency</div>
            <div className="h-8 w-8 rounded-xl bg-brand-50 flex items-center justify-center">
              <Zap className="h-4 w-4 text-brand-700" aria-hidden />
            </div>
          </div>
          {overallEff != null ? (
            <>
              <div className={`text-2xl sm:text-[30px] font-black tracking-tight mt-1 ${effColor(overallEff)}`}>{overallEff}%</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {overallEff >= 100 ? 'Ahead overall' : overallEff >= 85 ? 'On track' : 'Behind pace'}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{effEmployees.length} crew members</div>
            </>
          ) : (
            <>
              <div className="text-2xl sm:text-[30px] font-black tracking-tight mt-1 text-gray-300">—</div>
              <div className="text-xs text-gray-500 mt-0.5">No time data yet</div>
            </>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 -mt-2">
        Profit here is revenue minus what the crew is owed, from the pay ledger. The whole picture — job costs and overhead too — is "this month so far" in the money strip above.
        {stats?.ytd_jobs_without_pay > 0 && (
          <>
            {' '}
            <Link to="/jobs?filter=unpaid" className="text-amber-600 font-medium hover:underline">
              {stats.ytd_jobs_without_pay} finished {stats.ytd_jobs_without_pay === 1 ? 'job has' : 'jobs have'} nobody credited
            </Link>
            , so profit reads a little high.
          </>
        )}
      </p>

      {/* Winter work (Dec 1 – Mar 1): one line from mid-November, or while a winter job waits for its date. */}
      <WinterSummary />

      {/* ── Needs Attention — new leads, route problems, equipment, inbox ── */}
      <NeedsAttention compact />

      {/* ── Today's Jobs ── */}
      <div className="card">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-black uppercase tracking-tight text-gray-900">Today's jobs</h2>
          <Link to="/calendar" className="press text-sm font-bold text-brand-700 hover:text-brand-800 flex items-center gap-1 rounded-xl px-2 py-1 hover:bg-brand-50">
            Calendar <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : routeOrder.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No jobs scheduled today.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {routeOrder.map(key => {
              const groupJobs  = jobsByRoute[key] || [];
              const route      = key !== 'none' ? routeById[Number(key)] : null;
              const groupName  = route ? route.name : 'Other Jobs';
              const groupColor = route?.color || '#9ca3af';
              const doneCount  = groupJobs.filter(j => j.status === 'complete').length;
              const groupRevenue = groupJobs
                .filter(j => j.status === 'complete')
                .reduce((s: number, j: any) => s + (Number(j.price_snapshot) || 0), 0);
              const expanded = isExpanded(key);

              return (
                <div key={key}>
                  <button
                    onClick={() => toggleGroup(key)}
                    className="press w-full flex items-center px-4 sm:px-5 py-3.5 hover:bg-brand-50 text-left gap-3"
                  >
                    <div className="w-2 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: groupColor }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-black text-gray-900">{groupName}</p>
                      <p className="text-xs text-gray-500">
                        {doneCount}/{groupJobs.length} done
                        {groupRevenue > 0 && ` · ${fmtMoney(groupRevenue)} earned`}
                      </p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
                  </button>
                  {expanded && (
                    <ul className="divide-y divide-gray-50 border-t border-gray-100">
                      {groupJobs.map((job: any) => (
                        <li key={job.id}>
                          <Link to={`/jobs/${job.id}`} className="press flex items-center pl-11 pr-4 py-3 hover:bg-brand-50">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {job.property_label || job.address || job.customer_name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {job.customer_name}
                                {job.time_start  ? ` · ${fmtWindow(job)}`  : ''}
                                {job.price_snapshot ? ` · $${job.price_snapshot}` : ''}
                              </p>
                            </div>
                            <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold capitalize flex-shrink-0 ${statusColors[job.status] || 'bg-gray-100 text-gray-700'}`}>
                              {job.status.replace('_', ' ')}
                            </span>
                            <ChevronRight className="ml-2 h-4 w-4 text-gray-300 flex-shrink-0" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Equipment Reports ── */}
      {openReports.length > 0 && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <Wrench className="h-4 w-4 text-orange-500" />
            <h2 className="text-base font-black uppercase tracking-tight text-gray-900">Open Equipment Issues</h2>
            <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
              {openReports.length} open
            </span>
          </div>
          <ul className="divide-y divide-gray-100">
            {openReports.map((r: any) => (
              <li key={r.id} className="px-4 py-3 flex items-start gap-3">
                <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                  r.severity === 'down' ? 'bg-red-500' : r.severity === 'major' ? 'bg-orange-400' : 'bg-yellow-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{r.equipment_name}</p>
                  <p className="text-xs text-gray-500">
                    {r.crew_member_name || 'Unknown'} · {r.severity}
                    {r.job_address ? ` · ${r.job_address}` : ''}
                  </p>
                  {r.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{r.description}</p>}
                </div>
                <button
                  onClick={() => resolveEquip.mutate(r.id)}
                  disabled={resolveEquip.isLoading}
                  className="text-xs px-2.5 py-1 rounded-lg bg-green-50 text-green-700 font-semibold hover:bg-green-100 disabled:opacity-50 flex-shrink-0"
                >
                  Resolve
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Crew Efficiency ── */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1">
            <Zap className="h-4 w-4 text-amber-500" />
            <h2 className="text-base font-black uppercase tracking-tight text-gray-900">Crew Efficiency</h2>
            {overallEff != null && (
              <span className={`text-sm font-bold ml-1 ${effColor(overallEff)}`}>{overallEff}%</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEffDateMode('lifetime')}
              className={`press text-xs px-2.5 py-1 rounded-xl font-medium transition-colors ${effDateMode === 'lifetime' ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              All Time
            </button>
            <button
              onClick={() => setEffDateMode('date')}
              className={`press text-xs px-2.5 py-1 rounded-xl font-medium transition-colors ${effDateMode === 'date' ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              By Date
            </button>
            {effDateMode === 'date' && (
              <input
                type="date"
                value={effDate}
                onChange={e => setEffDate(e.target.value)}
                className="text-sm border border-gray-200 rounded px-2 py-1 text-gray-700"
              />
            )}
          </div>
        </div>
        {effLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : effEmployees.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {effDateMode === 'lifetime' ? 'No completed jobs with time data yet.' : 'No completed jobs with time data on this date.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Employee</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Role</th>
                  <th className="px-4 py-3 text-right">Jobs</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Est.</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Actual</th>
                  <th className="px-4 py-3 text-right">Efficiency</th>
                  <th className="px-4 py-3 text-right hidden sm:table-cell">Δ min</th>
                  <th className="px-4 py-3 text-left hidden lg:table-cell">Routes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {effEmployees.map((emp: any) => {
                  const diff = emp.minutes_ahead_or_behind;
                  return (
                    <tr key={emp.employee_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{emp.employee_name}</td>
                      <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{roleLabel(emp.role)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{emp.jobs_completed}</td>
                      <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell">{fmtMins(emp.estimated_minutes)}</td>
                      <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell">{fmtMins(emp.actual_minutes)}</td>
                      <td className="px-4 py-3 text-right">
                        {emp.efficiency_pct != null ? (
                          <span className={`font-semibold ${effColor(emp.efficiency_pct)}`}>
                            {emp.efficiency_pct}%
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">
                        {emp.efficiency_pct != null ? (
                          <span className={`font-medium text-xs ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff >= 0 ? '+' : ''}{diff}m
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                        {(emp.routes_worked || []).join(', ') || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Crew Combinations ── */}
      {combos.length > 0 && (
        <div className="card">
          <button
            onClick={() => setCombosOpen(o => !o)}
            className="w-full px-6 py-4 border-b border-gray-200 flex items-center gap-2 hover:bg-gray-50"
          >
            <Users className="h-4 w-4 text-brand-500" />
            <h2 className="text-base font-black uppercase tracking-tight text-gray-900 flex-1 text-left">Crew Combination Efficiency</h2>
            <BarChart2 className="h-4 w-4 text-gray-300" />
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${combosOpen ? '' : '-rotate-90'}`} />
          </button>
          {combosOpen && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 text-left">Crew Combo</th>
                    <th className="px-4 py-3 text-right">Sessions</th>
                    <th className="px-4 py-3 text-right hidden md:table-cell">Est.</th>
                    <th className="px-4 py-3 text-right hidden md:table-cell">Actual</th>
                    <th className="px-4 py-3 text-right">Efficiency</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Routes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {combos.map((c: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {c.member_names.join(' + ')}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{c.sessions}</td>
                      <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell">{fmtMins(c.total_est_minutes)}</td>
                      <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell">{fmtMins(c.total_actual_minutes)}</td>
                      <td className="px-4 py-3 text-right">
                        {c.efficiency_pct != null ? (
                          <span className={`font-semibold ${effColor(c.efficiency_pct)}`}>{c.efficiency_pct}%</span>
                        ) : <span className="text-gray-400" title="Not enough measured time yet">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{c.routes.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Beyond mowing ── Growth, manager approvals and the lawn-plan
          shopping list sit below the V1 lists on purpose: the morning's
          routes, problems and broken equipment come first. */}

      {/* New customers, leads, close rate and what a customer costs — this month beside last. */}
      <GrowthCard />

      {/* Changes a manager proposed, waiting for the owner, sit at the top of Needs Attention above (Nov 2 2026). */}

      {/* Friday's bags and bottles for the lawn plans. Big on purpose. */}
      <PlanBuyCard />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Revenue and profit side by side, with the labour that separates them.
 *
 * Profit here is revenue minus what the crew is owed, from the pay ledger.
 * Overhead is not in the app yet, so this is not the bottom line — the card
 * says so rather than letting the number imply more than it means.
 */
function RevenueCard({ label, primary, secondary, secondaryLabel, labor, sub, color }: {
  label: string;
  primary: number | null | undefined;
  secondary: number | null | undefined;
  secondaryLabel: string;
  labor?: number | null;
  sub?: string;
  color: 'green' | 'emerald' | 'violet' | 'blue';
}) {
  const colors = {
    green:   { icon: 'bg-green-50',   text: 'text-green-700',   sub2: 'text-green-600'   },
    emerald: { icon: 'bg-emerald-50', text: 'text-emerald-700', sub2: 'text-emerald-600' },
    violet:  { icon: 'bg-brand-50',  text: 'text-brand-700',  sub2: 'text-brand-600'  },
    blue:    { icon: 'bg-blue-50',    text: 'text-blue-700',    sub2: 'text-blue-600'    },
  }[color];

  const secIsNeg = secondary != null && secondary < 0;

  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-start justify-between">
        <div className="text-xs font-black uppercase tracking-wide text-gray-600">{label}</div>
        <div className="h-8 w-8 rounded-xl bg-brand-50 flex items-center justify-center">
          <DollarSign className="h-4 w-4 text-brand-700" aria-hidden />
        </div>
      </div>
      <div className="text-2xl sm:text-[30px] font-black tracking-tight text-gray-900 mt-1">{fmtMoney(primary)}</div>
      {/* Estimated until billing is finished: revenue is from job prices, profit
          from the pay ledger with no overhead. Say so on the number itself. */}
      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">revenue <span className="ml-1 px-1 py-px rounded bg-amber-50 text-amber-700 normal-case tracking-normal" title="Estimated from job prices — billing isn't finished yet">est.</span></div>
      {secondary != null && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className={`text-lg font-black tracking-tight flex items-center gap-1 ${secIsNeg ? 'text-red-600' : colors.text}`}>
            <TrendingUp className="h-3.5 w-3.5" />{fmtMoney(secondary)}
          </div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{secondaryLabel} <span className="ml-1 px-1 py-px rounded bg-amber-50 text-amber-700 normal-case tracking-normal" title="Revenue minus crew pay; overhead isn't in the app yet">est.</span></div>
          {labor != null && labor > 0 && (
            <div className="text-[11px] text-gray-500 mt-0.5">after {fmtMoney(labor)} labour</div>
          )}
        </div>
      )}
      {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
}
